import {useMemo, useCallback, useEffect, useState} from 'react'
import {useGetProgramClassificationsQuery} from '@apis/settings'
import type {ProgramClassificationInterface} from '@interfaces/programClassification'
import {findProgramClassification} from '@app/util-functions'

const DEFAULT_COLOR = '#bdbdbd'
const DEFAULT_PAGE_SIZE = 50

interface UseProgramClassificationsOptions {
  paginated?: boolean
  pageSize?: number
}

export const useProgramClassifications = (
  options?: UseProgramClassificationsOptions,
): {
  classifications: ProgramClassificationInterface[]
  getColorByName: (name: string) => string
  getDisplayNameByName: (name: string) => string
  getClassificationByName: (name: string) => ProgramClassificationInterface | undefined
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  onSearch: (search: string) => void
  onLoadMore: () => void
  refetch: () => void
} => {
  const paginated = Boolean(options?.paginated)
  const limit = options?.pageSize ?? DEFAULT_PAGE_SIZE
  const [offset, setOffset] = useState(0)
  const [search, setSearch] = useState('')
  const [accumulatedClassifications, setAccumulatedClassifications] = useState<
    ProgramClassificationInterface[]
  >([])

  const {data, isLoading, isFetching, refetch} = useGetProgramClassificationsQuery({
    limit: paginated ? limit : 100,
    offset: paginated ? offset : 0,
    search: paginated && search ? search : undefined,
  })

  useEffect(() => {
    if (!paginated) {
      setAccumulatedClassifications([])
      return
    }

    if (!data?.results) {
      return
    }

    if (offset === 0) {
      setAccumulatedClassifications(data.results)
      return
    }

    setAccumulatedClassifications((prev) => {
      const byId = new Map(prev.map((item) => [item.id, item]))
      data.results.forEach((item) => byId.set(item.id, item))
      return Array.from(byId.values())
    })
  }, [data, offset, paginated])

  const classifications = useMemo(
    () => (paginated ? accumulatedClassifications : data?.results || []),
    [paginated, accumulatedClassifications, data],
  )

  const hasMore = paginated
    ? classifications.length < (data?.count ?? 0) && Boolean(data?.next)
    : false
  const isLoadingMore = paginated ? isFetching && offset > 0 : false

  const findClassification = useCallback(
    (value: string): ProgramClassificationInterface | undefined => {
      return findProgramClassification(value, classifications)
    },
    [classifications],
  )

  const getColorByName = useCallback(
    (name: string): string => {
      return findClassification(name)?.color || DEFAULT_COLOR
    },
    [findClassification],
  )

  const getDisplayNameByName = useCallback(
    (name: string): string => {
      return findClassification(name)?.display_name || name
    },
    [findClassification],
  )

  const getClassificationByName = useCallback(
    (name: string): ProgramClassificationInterface | undefined => {
      return findClassification(name)
    },
    [findClassification],
  )

  const onSearch = useCallback(
    (searchValue: string): void => {
      if (!paginated) {
        return
      }
      const normalizedSearch = searchValue.trim()
      if (normalizedSearch === search) {
        return
      }
      setSearch(normalizedSearch)
      setOffset(0)
      setAccumulatedClassifications([])
    },
    [paginated, search],
  )

  const onLoadMore = useCallback((): void => {
    if (!paginated || !hasMore || isFetching) {
      return
    }
    setOffset((prev) => prev + limit)
  }, [paginated, hasMore, isFetching, limit])

  return {
    classifications,
    getColorByName,
    getDisplayNameByName,
    getClassificationByName,
    isLoading: isLoading || isFetching,
    isLoadingMore,
    hasMore,
    onSearch,
    onLoadMore,
    refetch,
  }
}
